// src/ActivityLogsPage.js - Admin activity logs and audit trail
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { supabase } from './supabaseClient';

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch from email_logs (email activity) - without foreign key join to avoid RLS recursion
      const { data: emailLogs, error: emailError } = await supabase
        .from('email_logs')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(1000);

      if (emailError) console.error('Error fetching email logs:', emailError);

      // Fetch profiles separately to avoid RLS recursion
      const profileIds = [...new Set((emailLogs || []).map(log => log.profile_id).filter(Boolean))];
      const profileMap = new Map();
      if (profileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number')
          .in('id', profileIds);
        
        if (profiles) {
          profiles.forEach(p => profileMap.set(p.id, p));
        }
      }

      // Combine different log sources
      const combinedLogs = [
        ...(emailLogs || []).map(log => {
          const profile = log.profile_id ? profileMap.get(log.profile_id) : null;
          return {
            id: log.id,
            timestamp: log.sent_at,
            action: 'Email Sent',
            user: profile ? `${profile.first_name} ${profile.last_name}` : 'Unknown',
            phone_number: profile?.phone_number || '',
            details: `To: ${log.recipient}, Subject: ${log.subject}`,
            type: 'email',
          };
        }),
      ];

      // Sort by timestamp
      combinedLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setLogs(combinedLogs);
      setAllLogs(combinedLogs);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(`Error: ${err.message || 'Failed to fetch activity logs'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter logs
  useEffect(() => {
    let filtered = [...allLogs];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.user?.toLowerCase().includes(term) ||
        log.phone_number?.toLowerCase().includes(term) ||
        log.action?.toLowerCase().includes(term) ||
        log.details?.toLowerCase().includes(term)
      );
    }

    // Apply action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoffDate;
      switch (dateFilter) {
        case 'today':
          cutoffDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          cutoffDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          cutoffDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        default:
          cutoffDate = null;
      }
      if (cutoffDate) {
        filtered = filtered.filter(log => new Date(log.timestamp) >= cutoffDate);
      }
    }

    setLogs(filtered);
  }, [searchTerm, actionFilter, dateFilter, allLogs]);

  const clearFilters = () => {
    setSearchTerm('');
    setActionFilter('all');
    setDateFilter('all');
  };

  const hasActiveFilters = searchTerm || actionFilter !== 'all' || dateFilter !== 'all';

  const getActionColor = (action) => {
    if (action.includes('Email')) return 'primary';
    if (action.includes('Block')) return 'error';
    if (action.includes('Unblock')) return 'success';
    return 'default';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Activity Logs ({logs.length})
        </Typography>
        <Button onClick={fetchData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search logs..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="all">All Actions</MenuItem>
              <MenuItem value="Email Sent">Email Sent</MenuItem>
              <MenuItem value="User Blocked">User Blocked</MenuItem>
              <MenuItem value="User Unblocked">User Unblocked</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateFilter}
              label="Date Range"
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No activity logs found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    {log.timestamp
                      ? new Date(log.timestamp).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action}
                      size="small"
                      color={getActionColor(log.action)}
                    />
                  </TableCell>
                  <TableCell>{log.user || '-'}</TableCell>
                  <TableCell>{log.phone_number || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.details || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}



